import React, { ReactElement, useEffect, useCallback } from 'react';

import { useSubmitClaimWorkflow } from '../../claims-center/submit-claims/useSubmitClaimWorkflow';
import { useSelectedShopId } from '../../../hooks/useSelectedShop';
import { useClaimsShopCarriers } from './common/useClaimsShopCarriers';
import { useIsOrderItemsRequired } from './common/useIsOrderItemsRequired';
import useInvoiceExtraction from './common/useInvoiceExtraction';
import { useImportDocuments } from './common/useImportDocuments';
import { useDocumentsViewer } from './documents-viewer/useDocumentsViewer';

import { ClaimType, ClaimContext, OnChangeClaimContext, ClaimShopCarrier, SubmitClaimEntryPoint } from './types';
import { ClaimStepContainerProps } from './common/ClaimStepContainer';

import InvestigationStepContainer from './investigation/InvestigationStepContainer';
import PartialDamageStepContainer from './partial-damage/PartialDamageStepContainer';
import MissingItemStepContainer from './missing-item/MissingItemStepContainer';
import WholeDamageStepContainer from './whole-damage/WholeDamageStepContainer';
import CashOnDeliveryStepContainer from './cash-on-delivery/CashOnDeliveryStepContainer';

import Switch from '../../Wizard/Switch';
import ChooseClaimTypeStep from './ChooseClaimTypeStep';
import ChooseDamageTypeStep from './ChooseDamageTypeStep';
import OutsideOfDeadlineStep from './common/OutsideOfDeadlineStep';
import DeadlineShippingInfoStep from './common/DeadlineShippingInfoStep';
import WarehouseDeliveryConfirmationStep from './common/WarehouseDeliveryConfirmationStep';
import ManualDeadlineCheckStep from './common/ManualDeadlineCheckStep';
import InvestigationDeliveredToMailboxStep from './investigation/InvestigationDeliveredToMailboxStep';
import EasyShippingInfoStep from './common/EasyShippingInfoStep';
import SearchClaimInfoStep from './common/SearchClaimInfoStep';
import DocumentsViewer from './documents-viewer/DocumentsViewer';
import InvoiceStep from './common/InvoiceStep';
import AdditionalInfoStep from './common/AdditionalInfoStep';
import { StateValue } from 'xstate';
import DraftLoadingStep from './common/DraftLoadingStep';
import DORSigningLinkStep from './investigation/DORSigningLinkStep';
import { useQuery } from '@tanstack/react-query';
import container from '../../../container';
import { useClaimsShopSettings } from '../ClaimsShopSettingsContext';
import PictureUploadLinkStep from './common/PictureUploadLinkStep';
import { useFlag } from '@unleash/proxy-client-react';

interface StepContainerMap {
  [key: string]: React.ComponentType<ClaimStepContainerProps>;
}

const stepContainersByClaimType: StepContainerMap = {
  [ClaimType.INVESTIGATION]: InvestigationStepContainer,
  [ClaimType.PARTIAL_DAMAGE]: PartialDamageStepContainer,
  [ClaimType.WHOLE_DAMAGE]: WholeDamageStepContainer,
  [ClaimType.MISSING_ITEM]: MissingItemStepContainer,
  [ClaimType.CASH_ON_DELIVERY]: CashOnDeliveryStepContainer,
};

interface CreateClaimProps {
  claim: ClaimContext;
  nonClaimableMessage?: string;
  onChange?: OnChangeClaimContext;
  onSubmit?: () => void;
  onSaveDraft?: () => void;
  resetClaim?: () => void;
  onLoadShipmentByTrackingId?: (trackingId: string) => void;
  onResetShipmentSearch?: () => void;
  onChangeTrackingCode?: (code: string) => void;
  onClose?: (step: StateValue) => void;
  isSubmitting?: boolean; // indicates 2 things - search is in progress or claim is being submitted
  isClaimDraftSaving?: boolean;
  isDisabled?: boolean;
}

export interface StepProps extends ClaimStepContainerProps {
  shopId: number;
  claim: ClaimContext;
  nonClaimableMessage?: string;
  as: React.ComponentType<ClaimStepContainerProps> | typeof React.Fragment;
  onChange: OnChangeClaimContext;
  onResetClaim: () => void;
  onResetShipmentSearch?: () => void;
  onLoadShipmentByTrackingId?: (trackingId: string) => void;
  onChangeTrackingCode?: (code: string) => void;
  isManual: boolean;
  carriers: ClaimShopCarrier[];
}

// used by parcel finder and claims center
function CreateClaim(props: CreateClaimProps): ReactElement {
  const {
    claim,
    nonClaimableMessage,
    onChange = () => {},
    onSubmit,
    onSaveDraft,
    resetClaim = () => {},
    onLoadShipmentByTrackingId,
    onResetShipmentSearch,
    onChangeTrackingCode,
    isSubmitting, // indicates 2 things - search is in progress or claim is being submitted
    isClaimDraftSaving,
    isDisabled,
  } = props;

  const { carrierCode, carrierCountryCode, isOrderItemTaxRate } = claim;

  const shopId = useSelectedShopId();
  const shopSettings = useClaimsShopSettings();
  const shouldShowEDorStepAnyway = useFlag('one-touch-claiming-awareness');

  const { workflowStep, transition, next, back, canonicalWorkflowStep } = useSubmitClaimWorkflow({
    ...claim,
    shopSettings,
    shouldShowEDorStepAnyway,
  });

  const onResetClaim = (): void => {
    resetClaim();
  };

  const onSelectDamage = (): void => transition('SELECT_DAMAGE');

  const createOnSelectClaimType = (event: string, type: ClaimType) => (): void => {
    transition(event, { context: { type } });
    onChange({ type });

    // In Parcel Finder we trigger import after claim type is selected
    if (claim.entryPoint === SubmitClaimEntryPoint.PARCEL_FINDER) {
      importDocuments({ ...claim, type });
    }
  };

  const onSelectInvestigation = createOnSelectClaimType('SELECT_INVESTIGATION', ClaimType.INVESTIGATION);
  const onSelectPartialDamage = createOnSelectClaimType('SELECT_PARTIAL_DAMAGE', ClaimType.PARTIAL_DAMAGE);
  const onSelectWholeDamage = createOnSelectClaimType('SELECT_WHOLE_DAMAGE', ClaimType.WHOLE_DAMAGE);
  const onSelectMissingItem = createOnSelectClaimType('SELECT_MISSING_ITEM', ClaimType.MISSING_ITEM);
  const onSelectCashOnDelivery = createOnSelectClaimType('SELECT_CASH_ON_DELIVERY', ClaimType.CASH_ON_DELIVERY);
  const onClose = (): void => props.onClose?.(canonicalWorkflowStep);

  const { data: carriers = [] } = useClaimsShopCarriers();

  const carrierSlug = `${carrierCode}-${carrierCountryCode}`;
  const availableDorLanguagesQueryResult = useQuery<string[]>({
    queryKey: ['availableDorLanguages', shopId, carrierSlug, !!claim.isReturnShipment],
    queryFn: () => container.touchpointsApi.getAvailableDorLanguages(shopId, carrierSlug, !!claim.isReturnShipment),
    enabled: Boolean(shopId && carrierCode && carrierCountryCode),
  });

  // finds selected carrier and adds it to the claim context
  useEffect(() => {
    const selectedCarrier = carriers.find(
      ({ code, countryCode }) => code === claim.carrierCode && countryCode === claim.carrierCountryCode
    );
    onChange({
      carrier: selectedCarrier,
    });
  }, [carriers, claim.carrierCode, claim.carrierCountryCode, onChange]);

  const { isLoading: isLoadingIsOrderItemsRequired } = useIsOrderItemsRequired(claim, onChange);

  const { isExtractingInvoice, isFailedToExtractInvoice, extractInvoiceData } = useInvoiceExtraction(
    claim,
    onChange,
    !!isOrderItemTaxRate,
    claim.type === ClaimType.CASH_ON_DELIVERY
  );

  const { importDocuments, importDORState, importInvoiceState } = useImportDocuments({
    shopId,
    onChange,
    extractInvoiceData,
  });

  const { documentsViewerButtonElement, documentViewerFiles, isDocumentsViewerOpen } = useDocumentsViewer({
    claim,
    workflowStep,
  });

  const onSearchNextClick = useCallback(() => {
    // In Claims Center we trigger import after shipment is searched and claim context initialized (luckily)
    importDocuments(claim);
    next();
  }, [importDocuments, next, claim]);

  const stepProps: StepProps = {
    shopId,
    claim,
    carriers,
    nonClaimableMessage,
    as: (claim.type && stepContainersByClaimType[claim.type]) || React.Fragment,
    onChange,
    onNext: next,
    onBack: back,
    onClose,
    onSaveDraft,
    headerLeftElement: documentsViewerButtonElement,
    onResetClaim,
    onResetShipmentSearch,
    onLoadShipmentByTrackingId,
    onChangeTrackingCode,
    isClaimDraftSaving,
    isSubmitting,
    isDisabled,
    isManual: !claim.shipmentId,
    isLoadingIsOrderItemsRequired,
    isExtractingInvoice,
    isFailedToExtractInvoice,
    extractInvoiceData,
  };

  return (
    <React.Fragment>
      {isDocumentsViewerOpen && <DocumentsViewer files={documentViewerFiles} />}
      <Switch selected={workflowStep}>
        <DraftLoadingStep key="draft-loading" name="draft-loading" onDraftLoaded={importDocuments} {...stepProps} />
        <ChooseClaimTypeStep
          key="choose-claim-type"
          name="choose-claim-type"
          onClickDamage={onSelectDamage}
          onClickMissingItem={onSelectMissingItem}
          onClickInvestigation={onSelectInvestigation}
          onClickCashOnDelivery={onSelectCashOnDelivery}
          claim={claim}
          onClose={onClose}
        />
        <ChooseDamageTypeStep
          key="choose-damage-type"
          name="choose-damage-type"
          onClickPartialDamage={onSelectPartialDamage}
          onClickWholeDamage={onSelectWholeDamage}
          onBack={back}
          onClose={onClose}
        />
        <SearchClaimInfoStep
          key="search-claim-info"
          name="search-claim-info"
          {...stepProps}
          onNext={onSearchNextClick}
          onSaveDraft={null}
        />
        <ManualDeadlineCheckStep key="manual-deadline-check" name="manual-deadline-check" {...stepProps} />
        <EasyShippingInfoStep key="easy-shipping-info" name="easy-shipping-info" {...stepProps} />
        <OutsideOfDeadlineStep key="outside-of-deadline" name="outside-of-deadline" {...stepProps} />
        <DeadlineShippingInfoStep key="deadline-shipping-info" name="deadline-shipping-info" {...stepProps} />
        <InvestigationDeliveredToMailboxStep
          key="investigation-delivered-to-mailbox"
          name="investigation-delivered-to-mailbox"
          {...stepProps}
        />
        <WarehouseDeliveryConfirmationStep
          key="warehouse-delivery-confirmation-info"
          name="warehouse-delivery-confirmation-info"
          {...stepProps}
        />
        <InvoiceStep key="invoice-step" name="invoice-step" importInvoiceState={importInvoiceState} {...stepProps} />
        <InvoiceStep
          key="invoice-step-final"
          name="invoice-step"
          importInvoiceState={importInvoiceState}
          {...stepProps}
          onNext={onSubmit}
          isSubmitButton={true}
        />
        <AdditionalInfoStep
          key="additional-info-step"
          name="additional-info-step"
          importDORState={importDORState}
          {...stepProps}
          onNext={onSubmit}
          isSubmitButton={true}
        />
        <PictureUploadLinkStep key="picture-upload-link-step" name="picture-upload-link-step" {...stepProps} />
        <DORSigningLinkStep
          key="create-dor-step"
          name="create-dor-step"
          {...stepProps}
          availableDorLanguagesQueryResult={availableDorLanguagesQueryResult}
        />
      </Switch>
    </React.Fragment>
  );
}

export default React.memo(CreateClaim);
